import React, { MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { addDays, format, isAfter, isEqual, isSameDay } from 'date-fns';
import { ReactDatePickerProps } from 'react-datepicker';

import {
  isAfterWithShift,
  isEqualWithMaxShift,
  isEqualWithShift,
  isInInterval,
  isMaxRangeDates,
} from '../../utils';
import { Side } from '../../../../types';
import { CalendarProps } from '../../types';
import { MINIMUM_NIGHTS } from '../../constants';
import { getMidnightFromString } from '../../../../../../../../utils';

type RenderDayContentsProps = Pick<
  CalendarProps,
  | 'startDate'
  | 'endDate'
  | 'startShift'
  | 'endShift'
  | 'changeShift'
  | 'minDate'
  | 'isMobile'
  | 'excludeDates'
  | 'isFlexDatesOff'
>;

export const renderDayContents: (
  props: RenderDayContentsProps
) => ReactDatePickerProps['renderDayContents'] =
  ({
    startDate,
    endDate,
    startShift,
    endShift,
    changeShift,
    minDate = new Date(),
    isMobile = false,
    excludeDates,
    isFlexDatesOff = false,
  }) =>
  (day, d) => {
    const date = d as Date;
    const startCompareObj = {
      date,
      pickerDate: startDate,
      shift: startShift,
    };
    const endCompareObj = {
      date,
      pickerDate: endDate,
      shift: endShift,
    };

    const isEndRangeDay = isInInterval(endCompareObj);
    const isStartRangeDay = isInInterval(startCompareObj);

    /** смотрим входит ли ячейка в исключающие даты, засериваем, но не дизайблим */
    const isExclude = !!(
      d &&
      !isEndRangeDay &&
      !isStartRangeDay &&
      excludeDates?.find((excludeDate) =>
        isSameDay(new Date(getMidnightFromString(excludeDate.toString())), d)
      )
    );

    const shiftDay = isEndRangeDay ? endShift : startShift;
    const sideDate = isEndRangeDay ? 'right' : 'left';

    const isCurrentDay = isEqual(new Date(format(minDate, 'MM/dd/yyyy')), date);

    const startDateWithRightArrow = isEqualWithMaxShift({
      ...startCompareObj,
      side: 'right',
    });

    const startDateWithLeftArrow =
      isEqualWithMaxShift({
        ...startCompareObj,
        side: 'left',
      }) && isAfterWithShift({ pickerDate: startDate, shift: startShift, minDate });

    const endDateWithRightArrow =
      isEqualWithMaxShift({
        ...endCompareObj,
        side: 'right',
      }) && !isMaxRangeDates({ startDate, endDate, endShift });

    const endDateWithLeftArrow =
      isEqualWithMaxShift({
        ...endCompareObj,
        side: 'left',
      }) && isAfterWithShift({ pickerDate: endDate, shift: endShift });

    const isCrossed =
      !!startDate &&
      !!endDate &&
      isEqual(
        addDays(startDate, startShift?.right + MINIMUM_NIGHTS),
        addDays(endDate, -endShift?.left)
      );

    const isBorderLeft =
      (isEqualWithShift({ ...startCompareObj, side: 'left' }) &&
        (isCurrentDay || startShift.left > 1)) ||
      (isEqualWithShift({ ...endCompareObj, side: 'left' }) && (endShift.left > 1 || isCrossed));

    const isBorderRight =
      (isEqualWithShift({ ...startCompareObj, side: 'right' }) &&
        (startShift.right > 1 || isCrossed)) ||
      (isEqualWithShift({ ...endCompareObj, side: 'right' }) &&
        (endShift.right > 1 || isMaxRangeDates({ startDate, endDate, endShift })));

    const conditionRemoval =
      (isStartRangeDay && !!startDate && !isEqual(date, startDate)) ||
      (isEndRangeDay && !!endDate && !isEqual(date, endDate));

    const shiftControl = (side: Side) => (e: MouseEvent<any>) => {
      e.preventDefault();
      e.stopPropagation();

      if (changeShift && shiftDay && shiftDay[side] < 2) {
        changeShift({ [side]: shiftDay[side] + 1, sideDate });
      }
    };

    const removeShift = (e: MouseEvent<any>) => {
      e.preventDefault();
      e.stopPropagation();

      const activeDate = isEndRangeDay ? endDate : startDate;
      const dateOnTheRight = activeDate && isAfter(date, activeDate);
      const key = dateOnTheRight ? 'right' : 'left';

      if (changeShift && shiftDay && shiftDay[key] > 0) {
        changeShift({ [key]: shiftDay[key] - 1, sideDate });
      }
    };

    return (
      <StyledCell
        startShift={isStartRangeDay}
        endShift={isEndRangeDay}
        borderLeft={isBorderLeft}
        borderRight={isBorderRight}
        exclude={isExclude}
        isFlexDatesOff={isFlexDatesOff}
      >
        <span>{day}</span>
        {!isFlexDatesOff && (
          <>
            {(endDateWithRightArrow || (startDateWithRightArrow && !isCrossed)) && (
              <ShiftArrow
                $end={isEndRangeDay}
                onClick={shiftControl('right')}
                $isMobile={isMobile}
              />
            )}
            {(startDateWithLeftArrow || (endDateWithLeftArrow && !isCrossed)) && (
              <ShiftArrow
                $left
                $end={isEndRangeDay}
                onClick={shiftControl('left')}
                $isMobile={isMobile}
              />
            )}
            {conditionRemoval && (
              <ShiftRemovalBlock onClick={removeShift}>&#x2715;</ShiftRemovalBlock>
            )}
          </>
        )}
      </StyledCell>
    );
  };

const ShiftArrow = styled.div<{
  $end?: boolean;
  $left?: boolean;
  $isMobile: boolean;
}>`
  position: absolute;
  content: '';
  left: ${({ $left, $isMobile }) => ($left ? `${$isMobile ? -8 : -12}px` : '100%')};
  z-index: 1;
  width: 0;
  height: 0;
  border-top: ${({ $isMobile }) => `${$isMobile ? 22 : 37}px solid transparent`};
  border-bottom: ${({ $isMobile }) => `${$isMobile ? 22 : 11}px solid transparent`};
  border-left: ${({ $end, $isMobile }) =>
    `${$isMobile ? 8 : 12}px solid ${$end ? '#6abf40' : '#f8e71c'}`};
  transform: ${({ $left, $end }) => `scale(${$left ? -1 : 1}, ${$end ? -1 : 1})`};

  &:after {
    position: absolute;
    content: '';
    z-index: 1;
    width: 0;
    height: 0;
    border-top: ${({ $isMobile }) => `${$isMobile ? 20 : 35}px solid transparent`};
    border-bottom: ${({ $isMobile }) => `${$isMobile ? 20 : 10}px solid transparent`};
    border-left: ${({ $end, $isMobile }) =>
      `${$isMobile ? 7 : 11}px solid ${$end ? '#cce763' : '#fffbe5'}`};
    right: 1px;
    bottom: ${({ $isMobile }) => `${$isMobile ? -20 : -10}px`};
  }

  &:before {
    content: '+';
    position: absolute;
    font-size: 16px;
    font-weight: normal;
    color: ${({ $end }) => ($end ? '#9DB63D' : '#cecab7')};
    right: ${({ $isMobile }) => `${$isMobile ? 4 : 8}px`};
    bottom: -10px;
    z-index: 2;
  }
`;

const StyledCell = styled.div<{
  borderLeft?: boolean;
  borderRight?: boolean;
  endShift?: boolean;
  startShift?: boolean;
  exclude: boolean;
  isFlexDatesOff?: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: center;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
  ${({ borderLeft }) => borderLeft && `border-left: 1px solid;`};
  ${({ borderRight }) => borderRight && `border-right: 1px solid;`};
  ${({ startShift, endShift, isFlexDatesOff }) =>
    (startShift || endShift) &&
    css`
      font-weight: bold;
      color: #17181a;
      background-color: ${startShift ? '#fffbe5' : '#cce763'};
      ${isFlexDatesOff && `border: 1px solid;`};
      border-top: 1px solid;
      border-bottom: 1px solid;
    `};
  ${({ exclude }) => exclude && 'color: #c2c5cc'};
  border-color: ${({ startShift }) => (startShift ? '#f8e71c' : '#6abf40')};
`;

const ShiftRemovalBlock = styled.div`
  position: absolute;
  top: 0;
  font-size: 8px;
  line-height: 16px;
  color: #919499;
  padding: 0 8px;
`;
